/**
 * The find worker on the page (decision 41; part 19a log, principle 4, table T): one worker, kept between jobs for its
 * cache (T6), sent one job at a time. It is ended when it goes {@link STALL_MS} without a message while a job runs (T1:
 * a pattern that backtracks without end), or when it fails or sends what doesn't read (T4), and a new one starts for the
 * next job. Time the page spent hidden isn't the worker's (T2). A search waiting its turn is replaced by a newer one
 * (T3); a replace never is, nor a count (part 19b: each of today's counts stands against the one before, D9). The worker, the timers and the page's visibility are injected, so the logic runs in tests.
 */
import type { FindQuery, QueryProblem } from '@cockatrice/core';
import { readMessage } from './protocol.ts';
import type { ChapterRange, FindChapter, FindJob, OnlyMatch, ReplacedChapter } from './protocol.ts';

/** T1: how long a job may go without a message from the worker, in milliseconds. */
export const STALL_MS = 3_000;

/** A find worker, as far as the page drives it (a `Worker`). */
export interface FindWorker {
  addEventListener(type: 'message' | 'messageerror' | 'error', listener: (event: unknown) => void): void;
  postMessage(message: FindJob): void;
  terminate(): void;
}

/** What the host needs; everything with an effect is injected. */
export interface FindHostOptions {
  /** Starts a find worker. */
  readonly worker: () => FindWorker;
  /** `setTimeout` and `clearTimeout`. */
  readonly setTimer: (run: () => void, ms: number) => unknown;
  readonly clearTimer: (timer: unknown) => void;
  /** Whether the page is hidden (`document.hidden`). */
  readonly hidden: () => boolean;
  /** Calls `listener` when the page is shown or hidden; returns what stops it. */
  readonly onVisibility: (listener: () => void) => () => void;
}

/** What a job asks, without the ID the host gives it. */
export type FindRequest =
  | { readonly kind: 'search'; readonly query: FindQuery; readonly chapters: readonly FindChapter[]; readonly ranges: string | null }
  | { readonly kind: 'replace'; readonly query: FindQuery; readonly replacement: string; readonly chapters: readonly FindChapter[]; readonly only: OnlyMatch | null }
  | { readonly kind: 'count'; readonly chapters: readonly FindChapter[]; readonly selection: ChapterRange | null };

/**
 * What a job gave: a search's counts and ranges, a replace's chapters, a count's words (part 19b), no pattern (`refused`), the worker ended for its
 * silence (`stopped`, T1), a failure (`failed`, T4), or a newer search took its turn (`superseded`, T3).
 */
export type FindOutcome =
  | { readonly kind: 'found'; readonly counts: readonly number[]; readonly ranges: readonly number[] }
  | { readonly kind: 'replaced'; readonly chapters: readonly ReplacedChapter[] }
  | { readonly kind: 'counted'; readonly counts: readonly number[]; readonly selection: number | null }
  | { readonly kind: 'refused'; readonly problem: QueryProblem }
  | { readonly kind: 'stopped' }
  | { readonly kind: 'failed' }
  | { readonly kind: 'superseded' };

/** A job waiting its turn or running. */
interface Pending {
  readonly job: FindJob;
  readonly resolve: (outcome: FindOutcome) => void;
}

/** A message event's data; undefined for anything else. */
function dataOf(event: unknown): unknown {
  return typeof event === 'object' && event !== null ? Reflect.get(event, 'data') : undefined;
}

/** Table T: the find worker and its jobs. */
export class FindHost {
  readonly #options: FindHostOptions;
  #worker: FindWorker | null = null;
  #running: Pending | null = null;
  readonly #waiting: Pending[] = [];
  #nextId = 1;
  #stall: unknown = undefined;
  /** T2: whether the page was hidden since the running job's last message. */
  #hiddenSince = false;
  #stopWatching: (() => void) | null = null;

  constructor(options: FindHostOptions) {
    this.#options = options;
  }

  /** Runs `request` once every job before it has ended; never rejects. */
  run(request: FindRequest): Promise<FindOutcome> {
    return new Promise((resolve) => {
      const job: FindJob = { ...request, id: this.#nextId++ };
      if (job.kind === 'search') {
        // T3: a search still waiting is replaced by this one.
        for (let i = this.#waiting.length - 1; i >= 0; i -= 1) {
          const waiting = this.#waiting[i];
          if (waiting?.job.kind === 'search') {
            this.#waiting.splice(i, 1);
            waiting.resolve({ kind: 'superseded' });
          }
        }
      }
      this.#waiting.push({ job, resolve });
      this.#next();
    });
  }

  /** Ends the worker and every job (the page going away). */
  stop(): void {
    for (const waiting of this.#waiting.splice(0)) waiting.resolve({ kind: 'superseded' });
    this.#end({ kind: 'superseded' }, true);
  }

  /** Starts the next job when none runs. */
  #next(): void {
    if (this.#running !== null) return;
    const pending = this.#waiting.shift();
    if (pending === undefined) return;
    this.#running = pending;
    let worker: FindWorker;
    try {
      worker = this.#worker ?? this.#start();
    } catch {
      this.#end({ kind: 'failed' }, true);
      return;
    }
    this.#hiddenSince = this.#options.hidden();
    this.#stopWatching = this.#options.onVisibility(() => {
      if (this.#options.hidden()) this.#hiddenSince = true;
    });
    this.#restartStall();
    try {
      worker.postMessage(pending.job);
    } catch {
      this.#end({ kind: 'failed' }, true);
    }
  }

  /** A new worker, listened to. */
  #start(): FindWorker {
    const worker = this.#options.worker();
    worker.addEventListener('message', (event) => {
      this.#heard(worker, dataOf(event));
    });
    for (const type of ['error', 'messageerror'] as const) {
      worker.addEventListener(type, () => {
        if (this.#worker === worker) this.#end({ kind: 'failed' }, true);
      });
    }
    this.#worker = worker;
    return worker;
  }

  /** A message from `worker` about the running job: a tick restarts T1; an answer ends the job; anything else, T4. */
  #heard(worker: FindWorker, data: unknown): void {
    const running = this.#running;
    if (this.#worker !== worker || running === null) return;
    const message = readMessage(data, running.job);
    if (message === null) {
      this.#end({ kind: 'failed' }, true);
      return;
    }
    switch (message.kind) {
      case 'tick':
        this.#hiddenSince = this.#options.hidden();
        this.#restartStall();
        return;
      case 'found':
        this.#end({ kind: 'found', counts: message.counts, ranges: message.ranges }, false);
        return;
      case 'replaced':
        this.#end({ kind: 'replaced', chapters: message.chapters }, false);
        return;
      case 'counted':
        this.#end({ kind: 'counted', counts: message.counts, selection: message.selection }, false);
        return;
      case 'refused':
        this.#end({ kind: 'refused', problem: message.problem }, false);
        return;
      case 'failed':
        this.#end({ kind: 'failed' }, false);
    }
  }

  /** T1, T2: the running job's limit, set again while the page was hidden since its last message. */
  #restartStall(): void {
    this.#options.clearTimer(this.#stall);
    this.#stall = this.#options.setTimer(() => {
      if (this.#hiddenSince || this.#options.hidden()) {
        this.#hiddenSince = this.#options.hidden();
        this.#restartStall();
        return;
      }
      this.#end({ kind: 'stopped' }, true);
    }, STALL_MS);
  }

  /** Ends the running job with `outcome` (the worker too, when `kill`), then starts the next. */
  #end(outcome: FindOutcome, kill: boolean): void {
    this.#options.clearTimer(this.#stall);
    this.#stall = undefined;
    this.#stopWatching?.();
    this.#stopWatching = null;
    if (kill) {
      this.#worker?.terminate();
      this.#worker = null;
    }
    const running = this.#running;
    this.#running = null;
    running?.resolve(outcome);
    this.#next();
  }
}
